import React from 'react';
import clsx from 'clsx';
import './XSBox.css';
import { HtmlElementDefaults } from '../../type/HtmlElementDefaults';
import { Styles } from '../../type/Styles';

export type XSBoxInput = InputDefault | InputFlexRow | InputGrid | InputFlex;

type InputDefault = Shared & {};

type InputFlexRow = Shared & {
    type: 'row' | 'column';
    flex?: 'stretch' | 'apart' | 'start' | 'center' | 'end' | 1;
    align?: 'start' | 'center' | 'end';
    justify?: 'start' | 'center' | 'space-between' | 'end';
    gap?: Styles.Gap | 'section' | 'none';
    padding?: 'sm' | 'md' | 'lg' | 'xl';
};

type InputGrid = Shared & {
    type: 'grid';
    gap?: 'sm' | 'md' | 'lg' | 'none';
    colWidth?: number;
    columns?: number;
};

type InputFlex = Shared & {
    type: 'flex';
    dir?: 'row' | 'column';
    align?: 'start' | 'center' | 'end';
    justify?: 'start' | 'center' | 'space-between' | 'end';
    gap?: Styles.Gap | 'none';
    padding?: Styles.Gap;
    flexChildren?: boolean;
};

type Shared = HtmlElementDefaults & {
    as?: any;
    wrap?: React.ComponentType<any>;
    border?: Border | Border[];
    fullWidth?: boolean;
    fullHeight?: boolean;
    padding?: Styles.Gap;
};

type Border = 'top' | 'bottom' | 'left' | 'right';

export const XSBox = React.forwardRef((input: XSBoxInput, ref: any) => {
    const { children, fullWidth, fullHeight } = input;
    const className = ['XSBox'];
    const Element: any = input.as || 'div';
    let style = input.style;

    let type = 'type' in input ? input.type : undefined;

    let content = children;

    if ('gap' in input) {
        className.push(`gap-${input.gap || ''}`);

        if (!type) {
            type = 'row';
        }
    }

    if ('justify' in input) {
        className.push(`justify-${input.justify}`);

        if (!type) {
            type = 'row';
        }
    }

    if ('flex' in input) {
        className.push(`flex-${input.flex}`);

        if (!type) {
            type = 'row';
        }
    }

    if (type) {
        className.push(`XSBox-type-${type}`);
    }

    if ('align' in input) {
        className.push(`align-${input.align}`);
    }

    if ('padding' in input) {
        className.push(`padding-${input.padding}`);
    }

    if ('flexChildren' in input) {
        className.push(`flexChildren`);
    }

    if ('dir' in input) {
        className.push(`dir-${input.dir}`);
    }

    if (input.border) {
        (Array.isArray(input.border) ? input.border : [input.border]).forEach((side) => {
            className.push(`border-${side}`);
        });
    }

    if ('type' in input && input.type === 'grid') {
        if (typeof input.colWidth === 'number') {
            style = style || {};
            style.gridTemplateColumns = `repeat(auto-fill, minmax(${input.colWidth}px, 1fr))`;
        }

        if (typeof input.columns === 'number') {
            style = style || {};
            style.gridTemplateColumns = `repeat(${input.columns}, 1fr)`;
        }
    }

    const element = (
        <>
            <Element
                className={clsx(input.className, className, { fullWidth, fullHeight })}
                style={style}
                ref={ref}
                onClick={input.onClick}>
                {content}
            </Element>
        </>
    );

    if (input.wrap) {
        const Wrapper = input.wrap;
        return <Wrapper>{element}</Wrapper>;
    }

    return element;
});
